/**
 * Das Kontextmenue einer Bestellzeile - `openOrderContextMenu`/`closeOrderContextMenu`/
 * `runOrderContextAction` (index.html:7145-7186), das erzeugte Menue selbst 7148-7169. Div-Wurzel
 * `#orderContextMenu` (index.html:3817).
 *
 * ================= Wie diese Komponente eingehaengt wird =================
 *
 * Anders als das Ziehen (Task 15, `ZeilenUmzug.ts`, direkt in `BoardSeite`/`Bestellzeile`
 * verdrahtet) bleibt das Menue eine EXTERNE Naht - wie die beiden Spalteneditoren aus Task 14.
 * `BoardSeite`s `onKontextmenue`-Prop reicht Ereignis und Id unveraendert seit Task 13 nur durch.
 * Der Aufrufer haelt `{ bestellung, x, y } | null` als Zustand, setzt ihn im `onKontextmenue`-
 * Handler (`event.preventDefault()` gehoert DORTHIN, nicht in diese Komponente - `Bestellzeile.tsx`
 * reicht das rohe `MouseEvent` unveraendert weiter) und rendert
 * `<KontextMenue bestellung={...} x={...} y={...} onSchliessen={...} onAktion={...} />` als
 * Geschwister von `<BoardSeite>` (Einhaengepunkt, siehe Bericht).
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Der Titel faellt auf den NAMEN zurueck, bevor er den generischen Platzhalter „Bestellung"
 *    zeigt (`order.orderNo || order.name || 'Bestellung'`, 7150) - eine ANDERE Kette als die
 *    Namenszelle der Zeile selbst (`Bestellzeile.tsx`, dort „Ohne Bestellnummer" ohne Rueckfall auf
 *    den Namen).
 * 2. Der Untertitel ist `[order.name && order.orderNo ? order.name : '', order.supplier]
 *    .filter(Boolean).join(' · ')` (7151) - der Name erscheint nur, wenn ZUSAETZLICH eine
 *    Bestellnummer da ist. Ist beides leer, steht „Keine weiteren Angaben" (7156).
 * 3. „Automatisch einsortieren" erscheint NUR, wenn `order.boardGroupOverride` gesetzt ist (7163).
 * 4. Archivieren/Wiederherstellen tauschen Text UND Icon nach `order.archived` (7164).
 * 5. Der Loeschen-Knopf traegt zusaetzlich die Klasse `danger` (7165).
 * 6. Fuenf der neun Eintraege fuehren ein ZWEITES Icon NACH dem Beschriftungstext (main/tasks/docs/
 *    pay/calc/split) - die uebrigen drei (auto/archive/delete) nicht.
 * 7. Positionierung: `Math.max(8, Math.min(x, innerWidth - rect.width - 8))` bzw. dieselbe Formel
 *    fuer `y`/`innerHeight` (7167-7168) - IMMER mindestens 8px vom Rand, nie ausserhalb des
 *    Fensters. Gemessen wird NACH dem Zeichnen (`getBoundingClientRect`), wie im Original.
 * 8. Der erste Knopf bekommt den Fokus, sobald das Menue steht (7169).
 * 9. Schliesst bei Escape, Klick ausserhalb des Menues und Fensterverlust (`blur`) -
 *    index.html:14896-14921. Die dortige Prioritaetskette anderer Dialoge (Passwort, neue
 *    Bestellung, …) ist Sache von `App.tsx` (nicht angefasst) - diese Komponente kennt nur sich
 *    selbst.
 * 10. `runOrderContextAction` schliesst IMMER zuerst (`closeOrderContextMenu()`), bevor sie die
 *     Aktion ausloest (7180) - dieselbe Reihenfolge hier: `onSchliessen` vor `onAktion`.
 *
 * ================= Erweiterung ueber das Original hinaus =================
 *
 * Zwischen 2026-09-01 (Asana-Unterticket zu Task 12) und dem Auftrag "Löschen-Buttons zusammenlegen"
 * (ebenfalls 2026-09-01, verbindliche Entscheidung des Auftraggebers) fuehrte dieses Menue KURZZEITIG
 * ZWEI getrennte Loesch-Eintraege: "Bestellung löschen" (Aktion `"delete"`, entfernte NUR die
 * Hub-Zeile) und "EK-Bestellung in JTL löschen" (Aktion `"jtlDelete"`, eigenes Recht
 * `delete_jtl_order`, loeschte zusaetzlich den JTL-Beleg). Seither ist das EIN einziger
 * "Löschen"-Eintrag (weiterhin Aktion `"delete"`, `"jtlDelete"` entfaellt ersatzlos): traegt die
 * Bestellung einen JTL-Link, loest er IMMER den bestehenden JTL-Loeschweg aus (Grund-Dialog,
 * `pruefeLoeschbar`, JTL-Beleg UND Hub-Zeile verschwinden zusammen - `App.tsx#loeschenAusloesen`
 * entscheidet das), und der Eintrag erscheint dann NUR mit dem Recht `delete_jtl_order` - fehlt es,
 * ist "Löschen" fuer diese Bestellung KOMPLETT ausgeblendet, kein abgeschwaechter lokaler Loeschweg
 * (Entscheidung des Auftraggebers, Punkt 3: "keinen Weg mehr geben, eine JTL-verknuepfte Bestellung
 * nur lokal zu loeschen"). Ohne JTL-Link bleibt "Löschen" wie zuvor "Bestellung löschen" (Eigenheit
 * 5, `danger`-Klasse, kein zweites Icon), braucht dafuer aber seit der Nacharbeit "Löschen-Merge"
 * (Pruefrunde, Wichtig 1) das Recht `delete_orders` - bis dahin stand hier unbedingt sichtbar,
 * unabhaengig vom Recht (dieselbe Luecke, die `Drawer.tsx` an der aequivalenten Stelle nie hatte).
 *
 * ZWEITE Erweiterung, selbes Datum, ANDERER Auftrag ("Sammel-Statuswechsel", Asana "Auf der
 * Bestellübersicht Sammeländerungen für Status und Bereich"): der Eintrag „Status ändern für {N}
 * Bestellungen…" (Aktion `"bulkStatus"`) ist ebenfalls NEU, kein Bestandsvorbild - der Bestand kennt
 * keine Mehrfachauswahl. Sichtbar NUR, wenn die rechtsgeklickte Zeile TEIL der aktuellen
 * Mehrfachauswahl ist UND diese mindestens zwei Zeilen umfasst (`istInAuswahl && ausgewaehlteAnzahl
 * >= 2`, beide neue Props - eine Auswahl von genau EINER Zeile hat bereits den Zelleneditor bzw. die
 * gewoehnliche Statusspalte dafuer, ein Sammeldialog fuer eine einzelne Zeile waere Ballast). Diese
 * Komponente kennt die GESAMTE Auswahl nicht selbst (sie bekommt wie bei `"jtlDelete"` nur die
 * rechtsgeklickte `bestellung`) - `ausgewaehlteAnzahl` ist die Groesse der Auswahl-Kopie, die
 * `App.tsx` fuehrt (siehe dortiger Kopfkommentar zu `ausgewaehlteBestellungen`), `istInAuswahl` das
 * Ergebnis von deren `.includes(bestellung.id)`. `ausloesen("bulkStatus")` traegt wie jede andere
 * Aktion die `id` DIESER Zeile mit (Signatur `OrderKontextAktion, id: string` bleibt einheitlich) -
 * der Aufrufer (`App.tsx#kontextAktion`) ignoriert sie fuer diesen einen Fall bewusst und liest
 * stattdessen die gesamte Auswahl, siehe dortiger Kommentar. Weder `danger` (keine destruktive
 * Aktion) noch ein zweites Icon (wie `"archive"`/`"auto"`, nicht wie die Reiter-Eintraege).
 *
 * DRITTE Erweiterung, Kontotyp "Nur lesen" (Task 6 des Plans „Kontotyp Nur lesen"): mit `nurLeser`
 * (`state/nurLeser.ts`, einmal in `App.tsx` aus den Rechten berechnet) entfallen vier Eintraege. Drei davon
 * schrieben ohne Rechtepruefung und werden vom Server fuer ein Konto mit nur `view_*`-Rechten abgelehnt:
 * „Status ändern für {N} Bestellungen…", „Teillieferung / Split anlegen" und Archivieren/Wiederherstellen.
 * Der vierte, „Automatisch einsortieren", schreibt heute NICHTS (`App.tsx#kontextAktion` protokolliert nur), hebt aber
 * eine Zuordnung auf, sobald er angeschlossen ist, und soll dann kein Angebot fuer Nur-Leser sein.
 * Reine Aktionen, deshalb ausgeblendet statt gesperrt. Die fuenf „… öffnen"-Eintraege (Lesen) bleiben.
 * Eine Prop statt `istNurLeser(rechte)` im Rumpf: ohne Angabe behaelt das Menue sein bisheriges Bild (auch bei
 * einem Rechte-Set ohne Schreibrecht), die Entscheidung "Nur-Leser" faellt einmal in `App.tsx`.
 *
 * ================= Annahme =================
 *
 * Die Komponente rendert NICHTS, wenn `bestellung` fehlt (`return null`) - das Original haelt das
 * geschlossene `#orderContextMenu` als leeren Div im Baum und schaltet nur `open`/`aria-hidden`.
 * Wie schon bei den Spalteneditoren (Task 14, Annahme 1) macht das im Teilbaumvergleich keinen
 * Unterschied - hier gibt es ohnehin keine Baseline fuer den offenen Zustand (Bericht, B5).
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import type { Order } from "../../lib/types.ts";
import { orderListZustand } from "../../lib/orderList.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { Ikone } from "../Ikone.tsx";

/**
 * Die `data-action`-Werte aus `runOrderContextAction` (index.html:7178-7186), woertlich. `"delete"`
 * deckt seit dem Auftrag "Löschen-Buttons zusammenlegen" (2026-09-01) BEIDE frueheren Loeschwege ab
 * (siehe Kopfkommentar, "Erweiterung ueber das Original hinaus") - `"jtlDelete"` gab es zwischen-
 * zeitlich als eigenen Wert, ist seither ersatzlos entfallen.
 */
export type OrderKontextAktion =
  | "main"
  | "tasks"
  | "docs"
  | "pay"
  | "calc"
  | "split"
  | "split-undo"
  | "duplicate"
  | "auto"
  | "archive"
  | "delete"
  | "bulkStatus";

interface TabEintrag {
  aktion: "main" | "tasks" | "docs" | "pay" | "calc";
  icon: string;
  beschriftung: string;
}

/** index.html:7157-7161 - die fuenf Drawer-Tabs, jeweils mit Pfeil-Icon danach (Eigenheit 6). */
const TAB_EINTRAEGE: readonly TabEintrag[] = [
  { aktion: "main", icon: "clipboard-list", beschriftung: "Bestellung öffnen" },
  { aktion: "tasks", icon: "list-checks", beschriftung: "Tasks öffnen" },
  { aktion: "docs", icon: "files", beschriftung: "Dokumente öffnen" },
  { aktion: "pay", icon: "credit-card", beschriftung: "Zahlung öffnen" },
  { aktion: "calc", icon: "calculator", beschriftung: "EK öffnen" },
];

export interface KontextMenueProps {
  /** `null`/`undefined` -> geschlossen, siehe Annahme im Kopfkommentar. */
  bestellung: Order | null | undefined;
  /** `event.clientX`/`event.clientY` beim Oeffnen (index.html:7145-7146). */
  x: number;
  y: number;
  /**
   * Fuer die Sichtbarkeit von "Löschen" bei einer JTL-verknuepften Bestellung (siehe Kopfkommentar,
   * "Erweiterung ueber das Original hinaus") - dieselbe Signatur wie `Drawer.tsx`/`App.tsx` sonst
   * ueblich (`zustand.rechte`).
   */
  rechte: ReadonlySet<string>;
  /**
   * Groesse der aktuellen Mehrfachauswahl (`App.tsx`s Kopie `ausgewaehlteBestellungen.length`) -
   * traegt zusammen mit `istInAuswahl` die Sichtbarkeit von „Status ändern für {N} Bestellungen…",
   * siehe Kopfkommentar.
   */
  ausgewaehlteAnzahl?: number;
  /** Ist `bestellung.id` Teil der aktuellen Mehrfachauswahl? Siehe Kopfkommentar. */
  istInAuswahl?: boolean;
  /** Kontotyp "Nur lesen": kein Sammelstatus, kein Split, kein Archivieren, kein Einsortieren. Ohne Angabe: alle Eintraege. */
  nurLeser?: boolean;
  /** `closeOrderContextMenu()`, index.html:7137-7143. */
  onSchliessen?: () => void;
  /** `runOrderContextAction(action)`, index.html:7178-7186 - der Aufrufer loest die Wirkung aus. */
  onAktion?: (aktion: OrderKontextAktion, id: string) => void;
}

export function KontextMenue({
  bestellung,
  x,
  y,
  rechte,
  ausgewaehlteAnzahl = 0,
  istInAuswahl = false,
  nurLeser = false,
  onSchliessen,
  onAktion,
}: KontextMenueProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const offen = bestellung != null;

  // Eigenheit 7+8: NACH dem Zeichnen messen und positionieren, dann den ersten Knopf fokussieren -
  // dieselbe Reihenfolge wie `openOrderContextMenu` (misst das bereits gefuellte `innerHTML`).
  useLayoutEffect(() => {
    if (!offen || !menuRef.current) return;
    const knoten = menuRef.current;
    const rect = knoten.getBoundingClientRect();
    knoten.style.left = `${Math.max(8, Math.min(x, window.innerWidth - rect.width - 8))}px`;
    knoten.style.top = `${Math.max(8, Math.min(y, window.innerHeight - rect.height - 8))}px`;
    knoten.querySelector("button")?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, bestellung, x, y]);

  // Eigenheit 9: Escape, Klick ausserhalb, Fensterverlust - jeweils nur, solange offen.
  useEffect(() => {
    if (!offen) return;
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (ereignis.key === "Escape") onSchliessen?.();
    };
    const beiKlick = (ereignis: MouseEvent): void => {
      if (!menuRef.current?.contains(ereignis.target as Node)) onSchliessen?.();
    };
    const beiUnschaerfe = (): void => onSchliessen?.();
    document.addEventListener("keydown", beiTaste);
    document.addEventListener("click", beiKlick);
    window.addEventListener("blur", beiUnschaerfe);
    return () => {
      document.removeEventListener("keydown", beiTaste);
      document.removeEventListener("click", beiKlick);
      window.removeEventListener("blur", beiUnschaerfe);
    };
  }, [offen, onSchliessen]);

  if (!bestellung) return null;

  // AUFTRAG 07 (E4): KEIN Kontextmenue fuer eine in JTL geloeschte Bestellung. `Bestellzeile.tsx`
  // ruft `onKontextmenue` fuer solche Zeilen schon gar nicht mehr - diese Pruefung ist die ZWEITE
  // Verteidigungslinie: das Menue haengt nicht am Ereignis allein, sondern an der Bestellung, und
  // ein kuenftiger zweiter Aufrufer (z. B. eine Tastaturbedienung) darf es ebenso wenig oeffnen
  // koennen. Dieselbe Ableitung wie ueberall sonst (`orderListZustand`), kein zweites Verstaendnis
  // von "geloescht".
  if (orderListZustand(bestellung) === "deleted") return null;

  const id = bestellung.id as string;
  // Eigenheit 1.
  const titel = (bestellung.orderNo as string) || (bestellung.name as string) || "Bestellung";
  // Eigenheit 2.
  const untertitel = [
    bestellung.name && bestellung.orderNo ? (bestellung.name as string) : "",
    bestellung.supplier as string,
  ]
    .filter(Boolean)
    .join(" · ");
  const archiviert = !!bestellung.archived;
  // Sichtbarkeit von "Löschen" bei einer JTL-verknuepften Bestellung - WOERTLICH dieselbe Formel wie
  // `Drawer.tsx` (dieselbe Catch-All-Signatur `[feld: string]: unknown` macht `jtlOrderId` sonst
  // `unknown`, siehe dortiger Kommentar). MIT JTL-Link NUR mit dem Recht `delete_jtl_order`
  // (Entscheidung des Auftraggebers, Punkt 2/3: kein abgeschwaechter lokaler Loeschweg fuer eine
  // verknuepfte Bestellung).
  //
  // NACHARBEIT "Löschen-Merge" (Pruefrunde, Wichtig 1): OHNE JTL-Link stand hier bis dahin ein
  // hartes `true` - "Löschen" erschien unbedingt, UNABHAENGIG vom Recht `delete_orders`. Das war eine
  // Luecke gegenueber `Drawer.tsx`, das an der aequivalenten Stelle (`zeigeLoeschen`) schon immer
  // `delete_orders` prueft - wer keine Bestellungen loeschen darf, sah den Menuepunkt trotzdem und
  // konnte ihn ausloesen (`kontextAktion`/`loeschenAusloesen` in `App.tsx` haetten den Schreibvorgang
  // zwar am Server abgelehnt - 403 -, aber die Oberflaeche haette es erst gar nicht anbieten sollen).
  // Jetzt WOERTLICH dieselbe Ableitung wie `Drawer.tsx`.
  const darfLoeschen = hatRecht(rechte, "delete_orders");
  const darfJtlLoeschen = hatRecht(rechte, "delete_jtl_order");
  const darfDuplizieren = hatRecht(rechte, "edit_orders");
  const jtlOrderId = Number(bestellung.jtlOrderId);
  const hatJtlOrderId = Number.isSafeInteger(jtlOrderId) && jtlOrderId > 0;
  const zeigeLoeschen = hatJtlOrderId ? darfJtlLoeschen : darfLoeschen;
  // "Split rückgängig": nur an einer Teillieferung mit JTL-Kennung; die Route verlangt edit_orders und delete_jtl_order.
  const zeigeSplitRueckgaengig = !!bestellung.splitFrom && hatJtlOrderId && darfJtlLoeschen && darfDuplizieren;
  // Kontotyp "Nur lesen": siehe Kopfkommentar, dritte Erweiterung.
  const zeigeSammelStatus = !nurLeser && istInAuswahl && ausgewaehlteAnzahl >= 2;
  const zeigeSplit = !nurLeser;
  const zeigeArchivieren = !nurLeser;
  // „Automatisch einsortieren" (nur bei gesetztem Override) hebt eine Zuordnung auf; die Aktion ist noch nicht
  // angeschlossen (`App.tsx#kontextAktion` protokolliert nur), soll aber kein Angebot fuer Nur-Leser werden.
  const zeigeEinsortieren = !nurLeser && !!bestellung.boardGroupOverride;
  // Der Trennstrich vor dem unteren Block steht nur, wenn dahinter noch etwas folgt - fuer ein Konto mit
  // Schreibrechten ist das immer so (Split, Archivieren), nur der Nur-Leser kann bei leerem Block enden.
  const folgtUntererBlock =
    zeigeSplit ||
    zeigeSplitRueckgaengig ||
    darfDuplizieren ||
    zeigeEinsortieren ||
    zeigeArchivieren ||
    zeigeLoeschen;

  const ausloesen = (aktion: OrderKontextAktion): void => {
    // Eigenheit 10: erst schliessen, dann die Aktion melden.
    onSchliessen?.();
    onAktion?.(aktion, id);
  };

  return (
    <div ref={menuRef} className="order-context-menu open" role="menu" aria-hidden="false">
      <div className="order-context-head">
        <strong>{titel}</strong>
        <span>{untertitel || "Keine weiteren Angaben"}</span>
      </div>
      {TAB_EINTRAEGE.map((eintrag) => (
        <button
          key={eintrag.aktion}
          type="button"
          role="menuitem"
          onClick={() => ausloesen(eintrag.aktion)}
        >
          <span className="icon-label">
            <Ikone name={eintrag.icon} />
            {eintrag.beschriftung}
          </span>
          <Ikone name="arrow-right" />
        </button>
      ))}
      {zeigeSammelStatus ? (
        <>
          {/* Nachbesserung (Pruefung e9f0edb, Gering): eigener, sichtbar abgesetzter Block - ohne
              diesen Trennstrich unterschied sich der Eintrag von den fuenf Reiter-Eintraegen darueber
              nur durch das fehlende `arrow-right`-Icon. */}
          <div className="order-context-separator" />
          <button type="button" role="menuitem" onClick={() => ausloesen("bulkStatus")}>
            <span className="icon-label">
              <Ikone name="list-checks" />
              {`Status ändern für ${ausgewaehlteAnzahl} Bestellungen…`}
            </span>
          </button>
        </>
      ) : null}
      {folgtUntererBlock ? <div className="order-context-separator" /> : null}
      {zeigeSplit ? (
        <button type="button" role="menuitem" onClick={() => ausloesen("split")}>
          <span className="icon-label">
            <Ikone name="split" />
            Teillieferung / Split anlegen
          </span>
          <Ikone name="circle-plus" />
        </button>
      ) : null}
      {zeigeSplitRueckgaengig ? (
        <button type="button" role="menuitem" onClick={() => ausloesen("split-undo")}>
          <span className="icon-label">
            <Ikone name="split" />
            Split rückgängig
          </span>
        </button>
      ) : null}
      {darfDuplizieren ? (
        <button type="button" role="menuitem" onClick={() => ausloesen("duplicate")}>
          <span className="icon-label">
            <Ikone name="copy" />
            Bestellung duplizieren
          </span>
        </button>
      ) : null}
      {zeigeEinsortieren ? (
        <button type="button" role="menuitem" onClick={() => ausloesen("auto")}>
          <span className="icon-label">
            <Ikone name="refresh-cw" />
            Automatisch einsortieren
          </span>
        </button>
      ) : null}
      {zeigeArchivieren ? (
        <button type="button" role="menuitem" onClick={() => ausloesen("archive")}>
          <span className="icon-label">
            <Ikone name={archiviert ? "archive-restore" : "archive"} />
            {archiviert ? "Wiederherstellen" : "Archivieren"}
          </span>
        </button>
      ) : null}
      {zeigeLoeschen ? (
        <button className="danger" type="button" role="menuitem" onClick={() => ausloesen("delete")}>
          <span className="icon-label">
            <Ikone name="trash-2" />
            Löschen
          </span>
        </button>
      ) : null}
    </div>
  );
}
